<script setup lang="ts">
import { computed } from 'vue';
import { playlistLabelFor, useResolvedPlaylistName } from '@/composables/api/useResolvedPlaylistNames';
import { useOpenCurrentPlaylist } from '@/composables/useOpenCurrentPlaylist';
import RecordLink from '@/components/RecordLink.vue';

/**
 * The playlist a player reports, as one or two links.
 *
 * A check-in carries a filename, so this resolves it to the playlist's real
 * name. When the list only points at a bucket, the two names are SEPARATE
 * targets — "Inspector Demo Bucket List → Inspector Demo Bucket" is two links,
 * not one long one. The bucket is usually what you actually want, and reaching
 * it through the list was a click that told you nothing on the way.
 *
 * Shared by the player card and the inspector's Current Playlist row so they
 * cannot disagree about what a reported name resolves to, or about which parts
 * of it are clickable. Text size and colour are inherited from the host.
 */
const props = withDefaults(
    defineProps<{
        /** The name the player reported — a filename, or a bare list name. */
        playlistName: string | null;
        /** Scopes a generated playlist to this player's zone when resolving. */
        locationZonePlayerId: number | null;
        /** Wrap the list's name instead of truncating it, so a quick-search highlight in it is visible. */
        wrap?: boolean;
    }>(),
    { wrap: false }
);

const playlistName = computed(() => props.playlistName?.trim() || null);
const locationZonePlayerId = computed(() => props.locationZonePlayerId);

const { resolution } = useResolvedPlaylistName(playlistName, locationZonePlayerId);
const { openPlaylist, resolving } = useOpenCurrentPlaylist(locationZonePlayerId);

const sourceBucketId = computed(() => resolution.value?.source_bucket_id ?? null);
const sourceBucketName = computed(() => resolution.value?.source_bucket_name ?? null);

/** The list's own name once resolved, else the reported filename. */
const playlistLabel = computed(() => playlistLabelFor(resolution.value, playlistName.value));

/**
 * The reported filename, and only when it isn't already what's on screen —
 * once the list is found its real name is the label, and the check-in's
 * `0000…--41819.txt` becomes the thing worth hovering for. A tooltip that
 * repeats the visible text is noise, so an unresolved name gets none.
 */
const tooltip = computed(() => {
    if (!playlistName.value) return undefined;
    return playlistLabel.value === playlistName.value ? undefined : playlistName.value;
});

const LINK_CLASS =
    'hover:text-primary min-w-0 cursor-pointer text-left underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none disabled:cursor-wait';
</script>

<template>
    <span
        v-if="playlistLabel"
        class="inline-flex min-w-0 max-w-full items-baseline gap-1"
        :title="tooltip"
        data-testid="playlist-with-bucket"
    >
        <button
            type="button"
            :class="[LINK_CLASS, wrap ? 'wrap-anywhere' : 'truncate']"
            :disabled="resolving"
            data-testid="playlist-with-bucket-list"
            @click.stop="openPlaylist(playlistName)"
        >
            {{ playlistLabel }}
        </button>
        <template v-if="sourceBucketId !== null && sourceBucketName">
            <!-- Between the two links, and not part of either — the arrow is
                 not a click target of its own. -->
            <span class="text-muted-foreground shrink-0" aria-hidden="true">→</span>
            <!-- A link to the bucket's page, with the side-peek button on hover.
                 `inline` keeps that button out of the line until then. -->
            <RecordLink
                entity-id="Bucket"
                :record-id="sourceBucketId"
                :label="`bucket ${sourceBucketName}`"
                inline
                class="hover:text-primary"
                data-testid="playlist-with-bucket-bucket"
                >{{ sourceBucketName }}</RecordLink
            >
        </template>
    </span>
</template>
